import { useEffect } from 'react';
import { Layout, Spin } from 'antd';
import { userHooks } from 'hooks';
import { userServices } from 'services';
import { IRoute } from 'data';
import './PrivateRouteWithOrganization.scss';
import AppHeaderWithLogoOnly from 'v2/components/AppHeaderWithLogoOnly';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrganizations } from 'v2/store/organizationList/actions/FetchOrganizations';
import { RootState } from 'store/reducer';
import { StoreContext } from 'contexts';
import { Route } from 'react-router-dom';
import ErrorView from 'v2/components/error/ErrorView';
import NoOrganization from 'v2/components/organization/NoOrganization';
import { Organization } from 'data/terra/Organization';
import { getNoneStringItem } from 'util/localStorage';
import localStorageConstants from 'constants/localStorage';
import { selectOrganization } from 'v2/store/organizationList/actions/SelectOrganization';
import { redirectToLoginPage } from 'helpers/auth';

const PrivateRouteWithOrganization = (props: IRoute) => {
  useEffect(() => {
    // Tracking - Set userId when logged in
    const userInfo = userServices.getUserInfo();
    if (userInfo) {
      track('setUserId', userInfo.sub);
    }
  }, []);

  // Check if user is logged in or not
  if (!userServices.isLoggedIn()) {
    redirectToLoginPage();
    return null;
  }

  // Fetch global data
  const { currentUser } = userHooks.useUserInfo();

  // Show spin when fetching data
  if (!currentUser) {
    return <Spin className="app-spin" />;
  }

  return (
    <StoreContext.Provider
      value={{
        currentUser,
      }}
    >
      <OrganizationChecker {...props} />
    </StoreContext.Provider>
  );
};

const OrganizationChecker = ({ component: Component, ...rest }: IRoute) => {
  const dispatch = useDispatch();
  const {
    organizations,
    hasInitialized,
    error,
    selectedOrganization,
  } = useSelector((state: RootState) => state.organizationList);

  useEffect(() => {
    if (!hasInitialized) {
      initOrganizations();
    }
  }, [hasInitialized]);

  const initOrganizations = () => {
    dispatch(fetchOrganizations());
  };

  const setDefaultOrganization = (organizations: Organization[]) => {
    if (selectedOrganization) return;

    dispatch(selectOrganization(getDefaultOrganization(organizations)));
  };

  const getDefaultOrganization = (
    organizations: Organization[]
  ): Organization => {
    const lastSelectedOrganizationId = getNoneStringItem<number>(
      localStorageConstants.USER_LAST_SELECTED_ORGANIZATION_ID
    );
    if (lastSelectedOrganizationId) {
      const organization = organizations.find(
        item => item.id === lastSelectedOrganizationId
      );
      if (organization) {
        return organization;
      }
    }

    const userIamClientId = localStorage.getItem(
      localStorageConstants.USER_IAM_CLIENT_ID
    );
    if (userIamClientId) {
      const organization = organizations.find(
        item => item.clientId === userIamClientId
      );
      if (organization) {
        return organization;
      }
    }

    return organizations[0];
  };

  if (error) {
    return (
      <Layout className="app-layout">
        <AppHeaderWithLogoOnly />
        <div style={{ height: 128 }} />
        <ErrorView onButtonClick={initOrganizations} />
      </Layout>
    );
  }

  if (hasInitialized) {
    if (organizations.length === 0) {
      return <NoOrganization />;
    }

    setDefaultOrganization(organizations);

    return (
      <Route {...rest} render={routeProps => <Component {...routeProps} />} />
    );
  }

  return (
    <Layout className="app-layout">
      <AppHeaderWithLogoOnly />
      <Spin className="app-spin" />
    </Layout>
  );
};

export default PrivateRouteWithOrganization;
